/**
 * MagNews newsletter subscription with server-side captcha.
 *
 * MagNews enforces its own image captcha on the newsletter form page: a POST without a
 * valid `_mn_form_ctrl_<id>` + `_mn_form_ctrl_<id>_seed` pair is rejected on its server,
 * so the check cannot be bypassed from the client.
 *
 * The seed is issued by MagNews, not generated here. It is read from the MagNews form page
 * over CORS (`Access-Control-Allow-Origin: *`), which is also why submits go through fetch:
 * the response is readable, so a wrong code shows inline instead of navigating the visitor
 * to a raw MagNews error page.
 *
 * Exposes `window.MagnewsCaptcha`, shared by <magnews-form> and the account form.
 */
class MagnewsCaptcha {
    /**
     * @param {HTMLElement} container - Element rendered by `enjp-magnews-captcha.liquid`.
     */
    constructor(container) {
        this.container = container;
        this.endpoint = container.dataset.endpoint;
        this.codeInput = container.querySelector('[data-captcha-code]');
        this.seedInput = container.querySelector('[data-captcha-seed]');
        this.contentsInput = container.querySelector('[data-visible-contents]');
        this.image = container.querySelector('[data-captcha-image]');
        this.errorEl = container.querySelector('[data-captcha-error]');
        this.refreshButton = container.querySelector('[data-captcha-refresh]');

        this.handleRefresh = this.handleRefresh.bind(this);
        this.refreshButton?.addEventListener('click', this.handleRefresh);
    }

    destroy() {
        this.refreshButton?.removeEventListener('click', this.handleRefresh);
    }

    get loaded() {
        return Boolean(this.seedInput?.value);
    }

    /**
     * Fields MagNews needs back for the captcha to be validated.
     * @returns {Object} Field name / value pairs.
     */
    get fields() {
        return {
            [this.codeInput.name]: this.codeInput.value,
            [this.seedInput.name]: this.seedInput.value,
            _mn_visible_contents: this.contentsInput?.value || '',
        };
    }

    /**
     * Builds the captcha image URL for a seed, on the MagNews origin taken from the
     * endpoint so the host stays defined in one place.
     * @param {string} seed - Seed issued by MagNews.
     * @returns {string} Absolute captcha image URL.
     */
    imageUrl(seed) {
        const params = new URLSearchParams({
            type: '0',
            seed: seed,
            idc: this.image.dataset.idc,
            tpk: this.image.dataset.tpk,
            w: this.image.dataset.width,
            h: this.image.dataset.height,
            fore: '#' + this.image.dataset.foreground,
            back: '#' + this.image.dataset.background,
        });
        return `${new URL(this.endpoint).origin}/nl/captcha.jpg?${params}`;
    }

    /**
     * Reads the seed (and the field list MagNews expects back) out of a MagNews response.
     * @param {string} html - MagNews page markup.
     * @returns {boolean} Whether a seed was found.
     */
    applySeed(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const seed = doc.querySelector(`input[name="${this.seedInput.name}"]`)?.value;
        const contents = doc.querySelector('input[name="_mn_visible_contents"]')?.value;

        if (!seed) {
            this.showError(window.magnewsStrings?.captchaUnavailable);
            return false;
        }

        this.seedInput.value = seed;
        this.image.src = this.imageUrl(seed);
        if (contents && this.contentsInput) {
            this.contentsInput.value = contents;
        }
        return true;
    }

    /**
     * Fetches a fresh seed from MagNews.
     * @returns {Promise<boolean>} Whether a seed was obtained.
     */
    async loadSeed() {
        try {
            const response = await fetch(this.endpoint, {method: 'GET'});
            return this.applySeed(await response.text());
        } catch (error) {
            this.showError(window.magnewsStrings?.captchaUnavailable);
            return false;
        }
    }

    handleRefresh(event) {
        event.preventDefault();
        this.codeInput.value = '';
        this.hideError();
        this.loadSeed();
    }

    showError(message) {
        if (!this.errorEl || !message) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('tw-hidden');
        this.errorEl.setAttribute('aria-hidden', 'false');
    }

    hideError() {
        if (!this.errorEl) return;
        this.errorEl.classList.add('tw-hidden');
        this.errorEl.setAttribute('aria-hidden', 'true');
    }

    /**
     * Posts a subscription to MagNews and reports back what its server decided.
     * A rejected response carries a replacement seed, which is applied here because the
     * submitted one is spent either way.
     * @param {Object} fields - Field name / value pairs to send.
     * @returns {Promise<string|null>} Validation message, or null on success.
     */
    async subscribe(fields) {
        const {html, rejection} = await MagnewsCaptcha.post(this.endpoint, {
            ...fields,
            ...this.fields,
        });

        if (rejection !== null && html) {
            this.applySeed(html);
            this.codeInput.value = '';
        }
        return rejection;
    }

    /**
     * Posts to MagNews without needing a captcha instance.
     * @param {string} endpoint - MagNews form URL.
     * @param {Object} fields - Field name / value pairs to send.
     * @returns {Promise<{html: string|null, rejection: string|null}>} Response and verdict.
     */
    static async post(endpoint, fields) {
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {'Content-Type': 'application/x-www-form-urlencoded'},
                body: new URLSearchParams(fields),
            });
            const html = await response.text();
            return {html: html, rejection: MagnewsCaptcha.rejectionMessage(html)};
        } catch (error) {
            return {html: null, rejection: window.magnewsStrings?.submitError || ''};
        }
    }

    /**
     * MagNews re-renders the form when it rejects a submit; on success it answers with a
     * short redirect page instead, so the presence of the form separates the two.
     * @param {string} html - MagNews response markup.
     * @returns {string|null} Validation message, or null when the submit went through.
     */
    static rejectionMessage(html) {
        if (!html.includes('name="mncustomform"')) return null;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const messages = Array.from(doc.querySelectorAll('ul[id^="_mn_form_ctrl_"] li'))
            .map((item) => item.textContent.trim())
            .filter(Boolean);

        return messages.join(' ') || window.magnewsStrings?.captchaInvalid || '';
    }
}

window.MagnewsCaptcha = MagnewsCaptcha;

/**
 * The standalone newsletter forms: everything they post belongs to MagNews, so the whole
 * form is serialised and sent, and a success navigates to the thank-you page.
 */
class MagnewsForm extends HTMLElement {
    constructor() {
        super();
        this.handleSubmit = this.handleSubmit.bind(this);
        this.updateReveal = this.updateReveal.bind(this);
    }

    connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        const container = this.querySelector('[data-magnews-captcha]');
        this.captcha = container ? new MagnewsCaptcha(container) : null;
        this.revealEl = this.querySelector('[data-captcha-reveal]');
        this.formError = this.querySelector('[data-form-error]');

        this.form.addEventListener('submit', this.handleSubmit);

        // The captcha only shows once the rest of the form is filled in, which also defers
        // the seed request until the visitor is actually about to subscribe.
        if (this.captcha && this.revealEl) {
            this.form.addEventListener('input', this.updateReveal);
            this.form.addEventListener('change', this.updateReveal);
            this.updateReveal();
        } else if (this.captcha) {
            this.captcha.loadSeed();
        }
    }

    disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit);
        this.form?.removeEventListener('input', this.updateReveal);
        this.form?.removeEventListener('change', this.updateReveal);
        this.captcha?.destroy();
    }

    get endpoint() {
        return this.captcha?.endpoint || this.form.getAttribute('action');
    }

    /**
     * Every required field except the captcha itself, which is what gates its reveal.
     * @returns {Array<HTMLElement>} Required fields.
     */
    get requiredFields() {
        return Array.from(this.form.querySelectorAll('[required]')).filter(
            (field) => field !== this.captcha?.codeInput
        );
    }

    /**
     * Shows the captcha once every other required field is valid, and keeps `required` in
     * sync — a hidden required input would make `reportValidity()` fail with nothing to
     * point the visitor at.
     * @returns {void}
     */
    updateReveal() {
        const ready = this.requiredFields.every((field) => field.checkValidity());

        this.revealEl.classList.toggle('tw-hidden', !ready);
        this.captcha.codeInput.toggleAttribute('required', ready);

        if (ready && !this.captcha.loaded) {
            this.captcha.loadSeed();
        }
    }

    showError(message) {
        if (this.captcha) {
            this.captcha.showError(message);
            return;
        }
        if (!this.formError || !message) return;
        this.formError.textContent = message;
        this.formError.classList.remove('tw-hidden');
        this.formError.setAttribute('aria-hidden', 'false');
    }

    hideError() {
        if (this.captcha) {
            this.captcha.hideError();
            return;
        }
        if (!this.formError) return;
        this.formError.classList.add('tw-hidden');
        this.formError.setAttribute('aria-hidden', 'true');
    }

    setBusy(busy) {
        const submit = this.form.querySelector('[type="submit"]');
        if (submit) submit.disabled = busy;
        this.form.setAttribute('aria-busy', String(busy));
    }

    async handleSubmit(event) {
        event.preventDefault();

        if (!this.form.reportValidity()) return;

        this.hideError();
        this.setBusy(true);

        // `submitnl` is a submit input, which FormData leaves out unless it is the
        // submitter — MagNews needs it, so it is added back explicitly.
        const fields = {
            ...Object.fromEntries(new FormData(this.form)),
            submitnl: 'submit',
        };
        const rejection = this.captcha
            ? await this.captcha.subscribe(fields)
            : (await MagnewsCaptcha.post(this.endpoint, fields)).rejection;

        this.setBusy(false);

        if (rejection === null) {
            window.location.assign(this.dataset.successUrl);
            return;
        }

        this.showError(rejection);
        this.captcha?.codeInput.focus();
    }
}

customElements.define('magnews-form', MagnewsForm);
